import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DollarOutlined,
  FilePdfOutlined,
  LinkOutlined,
  MailOutlined,
  RollbackOutlined,
  UserOutlined
} from "@ant-design/icons"
import {
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  Space,
  Tag,
  Typography
} from "antd"
import dayjs from "dayjs"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"

import Icon from "../../../../Icon"
import { empty } from "../../../../constants/placeholders"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { getDeclineReasonForDisplay } from "../../../../utils/documents/document-decline-outcome.ts"
import { formatNumber } from "../../../../utils/number/formate.ts"
import { ALL_DATE_FORMATS } from "../../../TeamManagementPage/constants/date-formats.ts"
import {
  PROPOSAL_STATUSES,
  PROPOSAL_STATUSES_OBJ,
  type ProposalStatuses,
  canShareProposalByEmail
} from "../../constants/proposal-statuses-keys.ts"
import { emailProposalToClient } from "../../redux/proposals.thunks.ts"
import { type Proposal } from "../../types/types.ts"

const { Title, Text } = Typography

interface ProposalsViewHeaderProps {
  proposal: Proposal
  saving: boolean
  viewLeadAllowed?: boolean
  onApproveProposal: () => void
  onUnderRevision: () => void
  onCancelReview: () => void
  onDuplicateProposal: () => void
}

export function ProposalViewHeader({
  proposal,
  saving,
  viewLeadAllowed,
  onApproveProposal,
  onUnderRevision,
  onCancelReview,
  onDuplicateProposal
}: ProposalsViewHeaderProps) {
  const dispatch = useAppDispatch()
  const company = proposal?.company_data
  const submittedAttachment = proposal?.submitted_attachment
  const version = submittedAttachment?.full_version || proposal?.version_display
  const submittedBy = submittedAttachment?.uploaded_by_name
  const reviewedBy = proposal?.reviewed_by_name
  const statusData = PROPOSAL_STATUSES_OBJ?.[proposal?.status]

  const date = proposal?.reviewed_at || proposal?.submitted_for_review_at
  const isDeclined = proposal?.status === PROPOSAL_STATUSES.DECLINED
  const isCancelled = proposal?.status === PROPOSAL_STATUSES.CANCELLED
  const isUnderRevisionOnly = isDeclined && !isCancelled
  const isUploadedFile = submittedAttachment?.extra?.uploaded

  const displayReason = getDeclineReasonForDisplay(proposal?.decline_reason)

  const ALLOWED_STATUSES: ProposalStatuses[] = [
    PROPOSAL_STATUSES.PENDING_TO_REVIEW
  ]

  const isPendingReview = ALLOWED_STATUSES.includes(proposal.status)

  const contractMeta = [
    {
      icon: <UserOutlined />,
      label: reviewedBy ? "Reviewed by" : "Submitted by",
      value: reviewedBy || submittedBy || empty,
      bold: true
    },
    {
      icon: <CalendarOutlined />,
      value: date || empty
    },
    {
      icon: <ClockCircleOutlined />,
      value: date ? dayjs(date, ALL_DATE_FORMATS)?.format("h:mm A") : empty
    },
    {
      icon: <FilePdfOutlined />,
      label: "Version",
      value: version
    },
    {
      icon: <DollarOutlined />,
      value: formatNumber(proposal?.total_amount),
      bold: true
    }
  ]

  const kebabMenuItems: MenuProps["items"] = useMemo(() => {
    const list = []

    if (canShareProposalByEmail(proposal ?? {})) {
      list.push({
        key: "1",
        label: "Share via Email",
        onClick: () => dispatch(emailProposalToClient(proposal.id)),
        icon: <MailOutlined />
      })
    }

    if (viewLeadAllowed && proposal?.lead_id) {
      list.push({
        key: "2",
        label: (
          <Link target={"_blank"} to={RoutesPaths.lead.view(proposal.lead_id)}>
            Open Lead
          </Link>
        ),
        icon: <LinkOutlined />
      })
    }

    return list
  }, [
    dispatch,
    viewLeadAllowed,
    proposal.id,
    proposal.lead_id,
    proposal?.proposal_id,
    proposal?.status
  ])

  return (
    <Flex gap={"small"} vertical>
      <Flex wrap justify={"space-between"} align={"center"} gap={"middle"}>
        <Flex vertical>
          <Flex gap={"small"} align={"center"} wrap>
            <Title level={4}>
              {proposal?.company_data?.company_name || "No company name"}
            </Title>
            <Tag style={statusData?.style}>
              {statusData?.label || proposal?.status}
            </Tag>
          </Flex>

          <Flex wrap align={"center"} gap={3}>
            <Text type={"secondary"}>
              {company?.company_name || "No company name"}
            </Text>
            <span>&bull;</span>
            <Text type={"secondary"}>Proposal ID: {proposal?.proposal_id}</Text>
          </Flex>
          {(isDeclined || isPendingReview) && displayReason ? (
            <Text type="secondary" className="mt-1 max-w-2xl">
              <Text strong>Reason: </Text>
              {displayReason}
            </Text>
          ) : null}
        </Flex>

        <Space wrap>
          {isPendingReview && (
            <>
              <Button
                color={"green"}
                variant={"solid"}
                icon={<CheckOutlined />}
                disabled={saving}
                onClick={onApproveProposal}
              >
                Approve
              </Button>
              <Button
                color={"orange"}
                variant={"solid"}
                icon={<RollbackOutlined />}
                disabled={saving}
                onClick={onUnderRevision}
              >
                Under Revision
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                danger
                color={"danger"}
                variant={"solid"}
                disabled={saving}
                onClick={onCancelReview}
              >
                Cancel
              </Button>
            </>
          )}
          {isUnderRevisionOnly && !isUploadedFile && (
            <Button
              icon={<CopyOutlined />}
              variant={"solid"}
              disabled={saving}
              onClick={onDuplicateProposal}
            >
              Duplicate
            </Button>
          )}
          {kebabMenuItems?.length > 0 ? (
            <Dropdown
              disabled={saving}
              menu={{ items: kebabMenuItems }}
              trigger={["click"]}
            >
              <Button
                variant={"solid"}
                onClick={(e) => e.stopPropagation()}
                icon={<Icon type={"VerticalDots"} />}
              />
            </Dropdown>
          ) : null}
        </Space>
      </Flex>
      <Space size="large" wrap className="text-gray-600">
        {contractMeta.map((item, idx) => (
          <Space key={idx} size={4}>
            {item.icon}
            {item.label && <Text type="secondary">{item.label}</Text>}
            <Text strong={item.bold}>{item.value}</Text>
          </Space>
        ))}
      </Space>
    </Flex>
  )
}
